import { BaseApiService } from './baseApi';
import type { Section, ApiResponse } from '../../../../types';

export interface CreateSectionRequest {
  title: string;
  content: string;
  is_visible?: boolean;
  order_index?: number;
}

export class SectionsApiService extends BaseApiService {
  constructor() {
    super('/sections');
  }

  /**
   * Fetch toutes les sections
   */
  async getAll(): Promise<ApiResponse<Section[]>> {
    return this.requestGet<Section[]>('', false);
  }

  /**
   * Fetch les sections visibles uniquement
   */
  async getVisible(): Promise<ApiResponse<Section[]>> {
    return this.requestGet<Section[]>('/visible', false);
  }

  /**
   * Fetch une section par ID
   */
  async getById(id: string): Promise<ApiResponse<Section>> {
    return this.requestGet<Section>(`/${id}`, false);
  }

  /**
   * Fetch une section par titre
   */
  async getByTitle(title: string): Promise<ApiResponse<Section>> {
    return this.requestGet<Section>(`/title/${encodeURIComponent(title)}`, false);
  }

  /**
   * Create une nouvelle section
   */
  async create(data: CreateSectionRequest): Promise<ApiResponse<Section>> {
    return this.requestPost<Section>('', data);
  }

  /**
   * Update une section
   */
  async update(id: string, data: Partial<CreateSectionRequest>): Promise<ApiResponse<Section>> {
    return this.requestPut<Section>(`/${id}`, data);
  }

  /**
   * Delete par ID
   */
  async deleteById(id: string): Promise<ApiResponse<boolean>> {
    return this.requestDelete<boolean>(`/${id}`);
  }

  /**
   * Alias pour maintenir la compatibilité
   */
  async deleteSection(id: string): Promise<ApiResponse<boolean>> {
    return this.deleteById(id);
  }

  /**
   * Réorganiser les sections
   */
  async reorder(orders: { id: string; order_index: number }[]): Promise<ApiResponse<Section[]>> {
    return this.requestPost<Section[]>('/reorder', { sectionOrders: orders });
  }

  /**
   * Toggle la visibilité d'une section
   */
  async toggleVisibility(id: string): Promise<ApiResponse<Section>> {
    return this.requestPatch<Section>(`/${id}/visibility`);
  }

  /**
   * Get le nombre de sections
   */
  async getCount(): Promise<ApiResponse<{ total: number; visible: number; hidden: number }>> {
    return this.requestGet<{ total: number; visible: number; hidden: number }>('/stats/count');
  }

  /**
   * Valider l'intégrité des sections
   */
  async validate(): Promise<ApiResponse<any>> {
    return this.requestGet<any>('/validate');
  }

  /**
   * Corriger l'ordre des sections
   */
  async fixOrder(): Promise<ApiResponse<any>> {
    return this.requestPost<any>('/fix-order');
  }
}

export const sectionsApi = new SectionsApiService();
